/**
 * Scene-faithful gradient stop resolvers (DEFUSE S6 — one truth).
 *
 * The hatch gradient ramp core (`resolveGradientRamp`, `gradientRamp.ts`) is
 * generic: it takes a `StopColorResolvers` so each caller decides how a stop's
 * ACI / trueColor becomes `RgbColor`. The SCENE pipeline resolves them
 * FAITHFULLY — unlike the PDF plot path there is no white→black tweak (the
 * on-screen ramp shows true colours).
 *
 * This is the scene-domain one-truth shared by both scene gradient consumers
 * (rather than each copying the resolver instance):
 *   - the DXF→preview producer `state/dxfGeometry.ts` `resolveSceneGradient`
 *     (JSON-encodes the `__hatch_gradient` bag channel the legacy adapter parses);
 *   - the typed hatch adapter `scene3d/adapters/geom/hatchGeometry.ts`
 *     `resolveTypedSceneGradient`.
 *
 * The generic `#rrggbb` ⇄ RGB codec lives in the neutral `color/rgbCodec` leaf
 * (shared with the PDF pipe); only the scene-specific resolver POLICY lives here.
 */

import { hexToRgb } from "../../color/rgbCodec";
import { aciToHex } from "../../scene/aciColorMap";

import type { StopColorResolvers } from "./gradientRamp";

/**
 * Faithful (untweaked) scene colour resolvers handed to the shared
 * `resolveGradientRamp` core. Unlike the PDF resolvers, white stays white (the
 * on-screen ramp shows true colours — there is no white page to read against).
 * An out-of-range / unmapped ACI falls back to black.
 */
export const SCENE_STOP_RESOLVERS: StopColorResolvers = {
  hexToRgb,
  aciToRgb: (aci) => hexToRgb(aciToHex(aci) ?? "#000000"),
};
